import React, { useCallback, useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import axiosInstance from '../../../utils/axiosConfig';
import { Cargando, DIAS_LETRA, MESES, botonAzul, botonClaro, campo, capital, diaSemana, display, fechaLarga, iso, panel } from '../ui';

// Un mes chiquito del calendario del año
const Mes = ({ anio, mes, feriados, elegido, onElegir }) => {
    const primero = new Date(anio, mes, 1);
    const total = new Date(anio, mes + 1, 0).getDate();
    const hoy = iso(new Date());
    return (
        <div>
            <p className={`${display} text-sm font-semibold text-slate-800 mb-2`}>{capital(MESES[mes])}</p>
            <div className="grid grid-cols-7 gap-0.5 text-center">
                {DIAS_LETRA.map((l, i) => <span key={i} className="text-[10px] text-slate-400 pb-0.5">{l}</span>)}
                {Array.from({ length: diaSemana(primero) }, (_, i) => <span key={`h${i}`} />)}
                {Array.from({ length: total }, (_, i) => {
                    const d = new Date(anio, mes, i + 1);
                    const k = iso(d);
                    const feriado = feriados[k];
                    const finde = diaSemana(d) >= 5;
                    return (
                        <button
                            key={k} type="button" onClick={() => onElegir(k)} title={feriado || undefined}
                            aria-label={`${fechaLarga(k)}${feriado ? `: ${feriado}` : ''}`}
                            className={`h-7 rounded-md text-[11px] tabular-nums transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/60 ${
                                feriado ? 'bg-sura-blue text-white font-semibold'
                                    : elegido === k ? 'bg-cyan-100 text-cyan-800 font-semibold'
                                        : finde ? 'text-slate-300 hover:bg-slate-100' : 'text-slate-600 hover:bg-slate-100'
                            } ${k === hoy && !feriado ? 'ring-1 ring-cyan-400' : ''}`}
                        >
                            {i + 1}
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

const FeriadosVista = () => {
    const [anio, setAnio] = useState(new Date().getFullYear());
    const [dias, setDias] = useState(null);
    const [elegido, setElegido] = useState(null);
    const [motivo, setMotivo] = useState('');
    const [guardando, setGuardando] = useState(false);

    const cargar = useCallback(() => {
        axiosInstance.get('/api/asistencia/dias-no-laborables', { params: { anio } })
            .then(({ data }) => setDias(data.dias)).catch(() => setDias([]));
    }, [anio]);
    useEffect(() => { setDias(null); cargar(); }, [cargar]);

    const feriados = Object.fromEntries((dias || []).map((d) => [d.fecha, d.descripcion]));

    const agregar = async (e) => {
        e.preventDefault();
        setGuardando(true);
        try {
            await axiosInstance.post('/api/asistencia/dias-no-laborables', { fecha: elegido, descripcion: motivo });
            toast.success('Día agregado');
            setElegido(null);
            setMotivo('');
            cargar();
        } catch (err) {
            toast.error(err.response?.data?.error || 'No se pudo agregar el día');
        } finally {
            setGuardando(false);
        }
    };
    const quitar = async (fecha) => {
        try {
            await axiosInstance.delete(`/api/asistencia/dias-no-laborables/${fecha}`);
            setElegido(null);
            cargar();
        } catch (err) {
            toast.error('No se pudo quitar el día');
        }
    };

    return (
        <div className="space-y-6">
            <header className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <h1 className={`${display} text-3xl font-bold text-slate-900`}>Días no laborables</h1>
                    <p className="text-sm text-slate-500 mt-1">
                        Ese día nadie cuenta como ausente, y quien marque queda como que trabajó un día libre. Toca un día para agregarlo.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <button type="button" onClick={() => setAnio((a) => a - 1)} className={botonClaro} aria-label="Año anterior"><ChevronLeft size={16} /></button>
                    <span className={`${display} w-16 text-center text-lg font-semibold text-slate-800 tabular-nums`}>{anio}</span>
                    <button type="button" onClick={() => setAnio((a) => a + 1)} className={botonClaro} aria-label="Año siguiente"><ChevronRight size={16} /></button>
                </div>
            </header>

            {!dias ? <Cargando alto="h-96" /> : (
                <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_20rem]">
                    <div className={`${panel} p-6 grid gap-x-8 gap-y-6 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4`}>
                        {MESES.map((_, m) => (
                            <Mes key={m} anio={anio} mes={m} feriados={feriados} elegido={elegido} onElegir={setElegido} />
                        ))}
                    </div>

                    <aside className="space-y-4">
                        {elegido && (
                            <div className={`${panel} p-5 border-cyan-200`}>
                                <p className={`${display} text-base font-semibold text-slate-900`}>{fechaLarga(elegido)}</p>
                                {feriados[elegido] ? (
                                    <div className="mt-2 flex items-center justify-between gap-3">
                                        <p className="text-sm text-slate-600">{feriados[elegido]}</p>
                                        <button type="button" onClick={() => quitar(elegido)} className={`${botonClaro} text-rose-600`}>
                                            <Trash2 size={14} /> Quitar
                                        </button>
                                    </div>
                                ) : (
                                    <form onSubmit={agregar} className="mt-3 space-y-2">
                                        <input value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Motivo, por ejemplo: Día de la Juventud"
                                            className={`${campo} w-full`} required autoFocus aria-label="Motivo" />
                                        <div className="flex gap-2">
                                            <button type="submit" disabled={guardando} className={botonAzul}>Marcar como no laborable</button>
                                            <button type="button" onClick={() => setElegido(null)} className={`${botonClaro} border-transparent`}>Cancelar</button>
                                        </div>
                                    </form>
                                )}
                            </div>
                        )}
                        <div className={`${panel} p-5`}>
                            <p className="text-sm font-semibold text-slate-900 mb-3">
                                {dias.length ? `${dias.length} días en ${anio}` : `No hay días cargados en ${anio}`}
                            </p>
                            <ul className="space-y-2">
                                {dias.map((d) => (
                                    <li key={d.fecha} className="group flex items-baseline gap-3 text-sm">
                                        <button type="button" onClick={() => setElegido(d.fecha)} className="w-24 shrink-0 text-left text-slate-500 tabular-nums hover:text-slate-900">
                                            {new Date(`${d.fecha}T12:00:00`).toLocaleDateString('es-VE', { day: 'numeric', month: 'short' })}
                                        </button>
                                        <span className="text-slate-800 flex-1">{d.descripcion}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </aside>
                </div>
            )}
        </div>
    );
};

export default FeriadosVista;
